import type { DetectionClass, ModelProvider, PerceptronParams } from "@protontech/autofill/types";
import type { Result } from "@protontech/autofill/types/utils";
import { unwrapOrThrow } from "@protontech/autofill/types/utils";
import { newPerceptronInferrer } from "@protontech/ml-inference";
import { buildModelProvider } from "../model_provider";
import type { ValidationProblem } from "../validate_weights";
import { validatePerceptronWeights } from "../validate_weights";
import EmailParamsJson from "./params/email-model.json";
import LoginParamsJson from "./params/login-model.json";
import NewPasswordParamsJson from "./params/new-password-model.json";
import OtpParamsJson from "./params/otp-model.json";
import PasswordChangeParamsJson from "./params/password-change-model.json";
import PasswordParamsJson from "./params/password-model.json";
import RecoveryParamsJson from "./params/recovery-model.json";
import RegisterParamsJson from "./params/register-model.json";
import UsernameHiddenParamsJson from "./params/username-hidden-model.json";
import UsernameParamsJson from "./params/username-model.json";

const paramsProvider: Record<DetectionClass, PerceptronParams> = {
  "new-password": NewPasswordParamsJson as PerceptronParams,
  "password-change": PasswordChangeParamsJson as PerceptronParams,
  "username-hidden": UsernameHiddenParamsJson as PerceptronParams,
  email: EmailParamsJson as PerceptronParams,
  login: LoginParamsJson as PerceptronParams,
  otp: OtpParamsJson as PerceptronParams,
  password: PasswordParamsJson as PerceptronParams,
  recovery: RecoveryParamsJson as PerceptronParams,
  register: RegisterParamsJson as PerceptronParams,
  username: UsernameParamsJson as PerceptronParams,
};

const makePerceptronInferrer = (params: PerceptronParams) => newPerceptronInferrer({ bias: params.bias, weights: new Map(params.coeffs) });

export const perceptronModelProvider: ModelProvider = unwrapOrThrow(buildModelProvider(paramsProvider, makePerceptronInferrer));

/** Constructs a ModelProvider from a perceptron weight set fetched at runtime (e.g. a downloaded
 * model artifact). Returns `ok: false` with the list of problems if the weights don't match the
 * current feature store. */
export const createPerceptronModelProvider = (weights: Record<DetectionClass, PerceptronParams>): Result<ModelProvider, ValidationProblem[]> => {
  const validation = validatePerceptronWeights(weights);
  if (!validation.ok) return validation;
  return { ok: true, value: unwrapOrThrow(buildModelProvider(weights, makePerceptronInferrer)) };
};
